import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { nationalIdText } from '@/lib/national-id';
import { useNavigate } from 'react-router-dom';
import { Search, UserCheck } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { activeProfile } from '@/lib/country';
import { dateOnly } from '@/lib/utils';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, SkeletonText, Input } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { sexText } from './patient-labels';

export interface PersonMatch {
  personId: string;
  uhid: string;
  fullName: string;
  sex: string;
  dob: string | null;
  approximateAgeYears: number | null;
  cnic: string | null;
  idType?: string | null;
  idCountry?: string | null;
  phone: string | null;
  knownAtThisFacility: boolean;
  patientIdHere: string | null;
  facilityCount: number;
}

export function personFacts(person: Pick<PersonMatch, 'sex' | 'dob' | 'approximateAgeYears' | 'facilityCount'>): string {
  return [
    sexText(person.sex),
    person.dob ? tr('opd', 'person_born_x', { date: dateOnly(person.dob) }) : null,
    person.approximateAgeYears !== null ? tr('opd', 'person_about_years', { count: person.approximateAgeYears }) : null,
    tr('opd', 'person_seen_at_facilities', { count: person.facilityCount }),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function PersonLookupPanel({
  selected,
  onSelect,
  onClear,
  onAssociate,
  associating,
}: {
  selected: PersonMatch | null;
  onSelect: (person: PersonMatch) => void;
  onClear: () => void;
  onAssociate?: (person: PersonMatch) => void;
  associating?: boolean;
}) {
  const navigate = useNavigate();
  const [cnic, setCnic] = useState('');
  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<PersonMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    setError(null);
    setSearching(true);
    try {
      setMatches(await get<PersonMatch[]>(`/patients/person-lookup${qs({ cnic: cnic || undefined, phone: phone || undefined })}`));
    } catch (caught) {
      setMatches(null);
      setError(describeError(caught));
    } finally {
      setSearching(false);
    }
  };

  if (selected) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-4">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-sm font-medium">
              <UserCheck className="h-4 w-4" aria-hidden /> {tr('opd', 'person_registering_x', { name: selected.fullName })}
            </p>
            <p className="text-sm text-muted-foreground">{tr('opd', 'person_known_on_platform', { uhid: selected.uhid, count: selected.facilityCount })}</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onClear}>
            {tr('opd', 'register_someone_else')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'has_this_person_been_here_or_anywhere_before')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {tr('opd', 'search_the_guardians_cnic_or_mobile_number')}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={nationalIdText().label}>
            <Input value={cnic} onChange={(event) => setCnic(event.target.value)} placeholder={nationalIdText().placeholder} />
          </Field>
          <Field label={tr('opd', 'mobile')}>
            <PhoneText value={phone} onChange={(event) => setPhone(event.target.value)} placeholder={activeProfile().phone.example || undefined} />
          </Field>
          <div className="flex items-end">
            <Button type="button" variant="outline" className="w-full" onClick={search} loading={searching}>
              <Search className="h-4 w-4" /> {tr('opd', 'search')}
            </Button>
          </div>
        </div>

        {error && <Alert variant="destructive">{error}</Alert>}

        {searching && (
          <SkeletonText lines={3} />
        )}

        {matches !== null && !searching && matches.length === 0 && (
          <Alert>{tr('opd', 'nobody_on_the_platform_carries_that_cnic_or')}</Alert>
        )}

        {matches !== null && matches.length > 0 && (
          <ul className="space-y-2">
            {matches.map((person) => (
              <li
                key={person.personId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{person.fullName}</span>
                    <Badge variant="muted">{person.uhid}</Badge>
                    {person.knownAtThisFacility && <Badge variant="success">{tr('opd', 'already_registered_here')}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{personFacts(person)}</p>
                </div>
                {person.knownAtThisFacility && person.patientIdHere ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/patients/${person.patientIdHere}`)}
                  >
                    {tr('opd', 'open_their_record')}
                  </Button>
                ) : onAssociate ? (
                  <Button type="button" size="sm" loading={associating} onClick={() => onAssociate(person)}>
                    {tr('opd', 'take_them_on')}
                  </Button>
                ) : (
                  <Button type="button" size="sm" onClick={() => onSelect(person)}>
                    {tr('opd', 'this_is_them')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
